import { fetchAwsApi, getAuthenticatedAccountId, parseApiResponse } from '../security';
import type { AppLocale } from '../i18n/locale';

type UnknownRecord = Record<string, unknown>;

export type NotificationType = 'credits' | 'announcement';

export interface NotificationRecord {
  id: string;
  accountId: string | null;
  heading: string;
  message: string;
  createdAt: string;
  notificationType: NotificationType | null;
}

interface RawNotificationRecord {
  id?: unknown;
  account_id?: unknown;
  heading?: unknown;
  message?: unknown;
  created_at?: unknown;
  notification_type?: unknown;
}

interface CreateNotificationInput {
  heading: Record<string, string>;
  message: Record<string, string>;
  deletionDate?: string;
  notificationType: NotificationType;
  accountId?: string;
}

let notificationsRequest: Promise<unknown[]> | null = null;

const LOW_CREDITS_NOTIFICATION_CONTENT = {
  heading: {
    en: "You're running low on credits!",
    zh: '您的点数快用完了！',
    es: '¡Te quedan pocos créditos!',
    jp: 'クレジットが残り少なくなっています！',
  },
  message: {
    en: 'Your credits are running low. Please purchase more credits for your account to generate more AI try-on photos and videos.',
    zh: '您的积分余额不足。请为账户充值，以便生成更多 AI 试穿照片和视频。',
    es: 'Te quedan pocos créditos. Por favor, compra más créditos para tu cuenta para generar más fotos y vídeos de prueba virtual con IA.',
    jp: 'クレジットの残量が少なくなっています。AI試着写真や動画をさらに生成するには、アカウントにクレジットを追加購入してください。',
  },
} as const;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseJsonString(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeLocalizedText(value: unknown, locale: AppLocale) {
  if (typeof value !== 'string') {
    return '';
  }

  const parsedValue = parseJsonString(value);
  if (typeof parsedValue === 'string') {
    return parsedValue;
  }

  if (!isRecord(parsedValue)) {
    return value;
  }

  const localeCandidates = locale === 'ja'
    ? ['ja', 'jp', 'en', 'zh', 'es']
    : [locale, locale === 'zh' ? 'cn' : '', 'en', 'zh', 'es', 'jp', 'ja'];

  for (const candidate of localeCandidates) {
    if (!candidate) {
      continue;
    }

    const localizedValue = parsedValue[candidate];
    if (typeof localizedValue === 'string' && localizedValue.trim()) {
      return localizedValue.trim();
    }
  }

  const firstLocalizedEntry = Object.values(parsedValue).find(
    (entry): entry is string => typeof entry === 'string' && entry.trim().length > 0
  );

  return firstLocalizedEntry?.trim() ?? value;
}

function extractNotificationsPayload(payload: unknown): unknown[] {
  if (!isRecord(payload)) {
    return [];
  }

  const directNotifications = payload.notifications;
  if (Array.isArray(directNotifications)) {
    return directNotifications;
  }

  const nestedBody = payload.body;
  if (typeof nestedBody === 'string') {
    const parsedBody = parseJsonString(nestedBody);
    if (isRecord(parsedBody) && Array.isArray(parsedBody.notifications)) {
      return parsedBody.notifications;
    }
  }

  if (isRecord(nestedBody) && Array.isArray(nestedBody.notifications)) {
    return nestedBody.notifications;
  }

  return [];
}

function normalizeNotificationType(value: unknown): NotificationType | null {
  return value === 'credits' || value === 'announcement' ? value : null;
}

function normalizeNotification(value: unknown, locale: AppLocale): NotificationRecord | null {
  if (!isRecord(value)) {
    return null;
  }

  const rawNotification = value as RawNotificationRecord;
  const id = typeof rawNotification.id === 'string' ? rawNotification.id : '';
  const createdAt = typeof rawNotification.created_at === 'string' ? rawNotification.created_at : '';
  const heading = normalizeLocalizedText(rawNotification.heading, locale);
  const message = normalizeLocalizedText(rawNotification.message, locale);

  if (!id || (!heading && !message)) {
    return null;
  }

  return {
    id,
    accountId: typeof rawNotification.account_id === 'string' ? rawNotification.account_id : null,
    heading,
    message,
    createdAt,
    notificationType: normalizeNotificationType(rawNotification.notification_type),
  };
}

function fetchNotificationRecords() {
  if (notificationsRequest) {
    return notificationsRequest;
  }

  notificationsRequest = (async () => {
    const response = await fetchAwsApi('/api/manage_notifications', { method: 'GET' });
    const payload = await parseApiResponse(response);

    if (!response.ok) {
      return [];
    }

    return extractNotificationsPayload(payload);
  })().catch(() => []).finally(() => {
    notificationsRequest = null;
  });

  return notificationsRequest;
}

export async function fetchNotifications(locale: AppLocale): Promise<NotificationRecord[]> {
  const records = await fetchNotificationRecords();
  return records
    .map((notification) => normalizeNotification(notification, locale))
    .filter((notification): notification is NotificationRecord => notification !== null);
}

export async function sendLowCreditsNotification() {
  const accountId = getAuthenticatedAccountId();
  const response = await createNotification({
    ...LOW_CREDITS_NOTIFICATION_CONTENT,
    notificationType: 'credits',
    accountId,
  });
  const payload = await parseApiResponse(response);

  if (!response.ok) {
    throw new Error(
      typeof (payload as { message?: unknown })?.message === 'string'
        ? (payload as { message: string }).message
        : 'Unable to create low credit notification.'
    );
  }
}

export async function manageNotifications() {
  return fetchAwsApi('/api/manage_notifications', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function deleteNotification(notificationId: string) {
  return fetchAwsApi('/api/manage_notifications', {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      notification_id: notificationId,
    }),
  });
}

export async function createNotification(input: CreateNotificationInput) {
  const body = {
    heading: input.heading,
    message: input.message,
    notification_type: input.notificationType,
    ...(input.accountId ? { account_id: input.accountId } : {}),
    ...(input.deletionDate ? { deletion_date: input.deletionDate } : {}),
  };

  return fetchAwsApi('/api/manage_notifications', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
}
